import type { ReactNode } from "react";

import SummarySection from "./sections/SummarySection";
import CashflowSection from "./sections/CashflowSection";
import IncomeSection from "./sections/IncomeSection";
import SpendSection from "./sections/SpendSection";
import LossesSection from "./sections/LossesSection";
import FinancialStatementsSection from "./sections/FinancialStatementsSection";
import ImportSection from "./sections/ImportSection";
import InboxSection from "./sections/InboxSection";
import PlatformsSection from "./sections/PlatformsSection";
import BudgetsSection from "./sections/BudgetsSection";
import BudgetLinesSection from "./sections/BudgetLinesSection";
import AccessSection from "./sections/AccessSection";
import AccountsSection from "./sections/AccountsSection";
import { BUDGET_LINES_ID } from "./lib/budgetRoutes";

// ---------------------------------------------------------------------------
// Navegación de la app "Gastos y Presupuestos" (#615). Alimenta el sub-menú
// (ExpensesLayout) y las rutas (routes.tsx).
//
// Dos ítems tienen hijos, y los hijos son rutas planas (`/expenses/<id>`), igual
// que el resto:
//  • «Flujo»: su portada analiza ingresos, gastos y pérdidas juntos, y cada hijo
//    abre el detalle de una de las tres piezas.
//  • «Presupuestos»: su página (`/expenses/budgets`) ES el Dashboard, así que se
//    pinta además como primer sub-ítem (`self`) junto a «Desglose». No es otra
//    ruta ni otra llave de derechos: es la misma página con nombre propio.
// ---------------------------------------------------------------------------

export interface ExpensesNavItem {
  id: string;
  labelKey: string;
  icon: string;
  element: ReactNode;
  children?: ExpensesNavItem[];
  /**
   * La página del propio ítem, pintada como su primer sub-ítem. Comparte id,
   * ruta y llave (`menu:expenses.<id>`) con el padre: no agrega nada al catálogo.
   */
  self?: { labelKey: string; icon: string };
}

export const EXPENSES_BASE = "/expenses";

/** Ítem «Acceso»: se oculta del menú donde la lista de dueños es fija. */
export const ACCESS_ID = "access";

export const MENU: ExpensesNavItem[] = [
  { id: "summary", labelKey: "expenses.menu.summary", icon: "ri-bar-chart-box-line", element: <SummarySection /> },
  {
    id: "cashflow",
    labelKey: "expenses.menu.cashflow",
    icon: "ri-exchange-dollar-line",
    element: <CashflowSection />,
    children: [
      { id: "income", labelKey: "expenses.menu.income", icon: "ri-arrow-down-circle-line", element: <IncomeSection /> },
      // Gastos + «¿Cuándo se gastó?» en una sola página.
      { id: "expenses", labelKey: "expenses.menu.expenses", icon: "ri-receipt-line", element: <SpendSection /> },
      { id: "losses", labelKey: "expenses.menu.losses", icon: "ri-error-warning-line", element: <LossesSection /> },
    ],
  },
  { id: "cash", labelKey: "expenses.menu.cash", icon: "ri-file-chart-line", element: <FinancialStatementsSection /> },
  { id: "import", labelKey: "expenses.menu.import", icon: "ri-magic-line", element: <ImportSection /> },
  { id: "accounts", labelKey: "expensesStatements:menu.accounts", icon: "ri-bank-card-line", element: <AccountsSection /> },
  { id: "inbox", labelKey: "expenses.menu.inbox", icon: "ri-mail-open-line", element: <InboxSection /> },
  { id: "platforms", labelKey: "expenses.menu.platforms", icon: "ri-cloud-line", element: <PlatformsSection /> },
  {
    id: "budgets",
    labelKey: "expenses.menu.budgets",
    icon: "ri-scales-3-line",
    element: <BudgetsSection />,
    self: { labelKey: "expenses.menu.budgetsDashboard", icon: "ri-dashboard-line" },
    children: [
      { id: BUDGET_LINES_ID, labelKey: "expenses.menu.budgetLines", icon: "ri-list-check-2", element: <BudgetLinesSection /> },
    ],
  },
  // En el menú solo sale donde la lista de acceso se puede editar (ExpensesLayout).
  { id: ACCESS_ID, labelKey: "expenses.menu.access", icon: "ri-shield-user-line", element: <AccessSection /> },
];

/** Todas las secciones con ruta: los ítems y sus hijos. */
export const SECTIONS: ExpensesNavItem[] = MENU.flatMap((item) => [item, ...(item.children || [])]);

/** Vista de «¿Cuándo se gastó?» dentro de Gastos (antes `/expenses/timeline`). */
export const SPEND_TIMELINE_VIEW = "timeline";

export const expensesDefaultRoute = `${EXPENSES_BASE}/${MENU[0].id}`;
